import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { backendURL, useApiFetch } from "../../utils/api";
import { useConfirmation } from "../../contexts/ConfirmationContext";
import { useNotifications } from "../../contexts/NotificationContext";
import { ShootingSession } from "../../types/ShootingSession";
import EventRankingTable from "../../components/EventRankingTable";
import "../../components/EventRankingTable.css";

interface EventRankingLocationState {
    competitionId: string;
    competitionDate: string;
    competitionName: string;
    shootingEventId: string;
    shootingEventName: string;
}

function EventRanking() {
    const navigate = useNavigate();
    const location = useLocation();
    const rankingData = location.state as EventRankingLocationState | null;
    const [shootingSessions, setShootingSessions] = useState<ShootingSession[]>([]);
    const apiFetch = useApiFetch();
    const { confirm } = useConfirmation();
    const { success } = useNotifications();

    const handleDeleteSession = async (session: ShootingSession) => {
        const confirmed = await confirm({ message: "Czy na pewno chcesz usunąć sesję?" });
        if (!confirmed) return;

        try {
            await apiFetch(`${backendURL}/shooting-sessions`, {
                method: "DELETE",
                includeAuth: true,
                body: JSON.stringify({
                    shootingSessionId: session.id,
                }),
            });
            setShootingSessions((prev) => prev.filter((s) => s.id !== session.id));
            success("Sesja została usunięta");
        } catch (err) {
            console.error("Error deleting session:", err);
        }
    };

    useEffect(() => {
        if (!rankingData) return;

        const fetchShootingSessions = async () => {
            try {
                const params = new URLSearchParams({
                    competitionId: rankingData.competitionId,
                    shootingEventId: rankingData.shootingEventId,
                });
                const data = await apiFetch(`${backendURL}/shooting-sessions/competition-sessions?${params}`, {
                    method: "GET",
                    includeAuth: true
                });
                setShootingSessions(data);
            } catch (err) {
                console.error("Error fetching shooting sessions:", err);
            }
        };
        fetchShootingSessions();
    }, [rankingData]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pl-PL');
    };

    return (
        <Container>
            <Row>
                <Col xl={8} md={10} xs={12} className="mx-auto">
                    <div className="py-3">
                        {rankingData ? (
                            <>
                                <h1 className="font-weight-light text-center mb-1">
                                    {rankingData.competitionName} {formatDate(rankingData.competitionDate)}
                                </h1>

                                <h3 className="font-weight-light text-center mb-2">{rankingData.shootingEventName}</h3>

                                <div className="d-flex justify-content-center mb-2">
                                    <Button className="px-5"
                                        variant="dark"
                                        onClick={() => navigate("/new-session", {
                                            state: {
                                                competitionId: rankingData.competitionId,
                                                competitionDate: rankingData.competitionDate,
                                                shootingEventId: rankingData.shootingEventId,
                                                competitionName: rankingData.competitionName,
                                            }
                                        })}
                                    >
                                        Dodaj sesję
                                    </Button>
                                </div>

                                <EventRankingTable
                                    sessions={shootingSessions}
                                    shootingEventId={rankingData.shootingEventId}
                                    competitionName={rankingData.competitionName}
                                    competitionDate={rankingData.competitionDate}
                                    showLastModified
                                    onDeleteSession={handleDeleteSession}
                                />
                            </>
                            
                        ) : (
                            <p className="text-center mb-4">Brak danych</p>
                        )}
                    </div>
                </Col>
            </Row>
        </Container>
    );
}

export default EventRanking;